/**
 * Product Builders registry — the REAL SR products, composing the now-live
 * modules: the verified brand kit (Artwork Studio), real pricing (Pricing
 * Engine), and the AI concept proof (Artwork `/api/artwork/generate`).
 *
 * No generic "banner builder". Each builder deep-links into the real modules:
 *   - `priceProduct` → a Pricing Engine CATALOG id (`/pricing?product=…`)
 *   - `artworkFlow`  → an Artwork Studio flow (`/artwork-studio?flow=…`)
 *
 * `engine` reflects the interactive all-in-one (3D) builder:
 *   'in-progress' = the compose links are LIVE (pricing + artwork wired); the
 *                   3D configurator runtime isn't bundled in this repo yet.
 *   'planned'     = product is real but its pricing line isn't ported here yet.
 */

export const BUILDERS = [
  {
    id: 'championship-ring',
    name: 'Championship Ring',
    tagline: 'The flagship title ring',
    engine: 'in-progress',
    priceProduct: 'rings',
    artworkFlow: 'champ-ring',
    inputs: ['Size & metal', 'Action bezel', 'Features & stones', 'Box & pendants', 'Quantity & comps'],
    composes: ['artwork', 'pricing'],
    blurb: 'Configure metal, bezel, and features against the live DB margin ladder, drop in the school mark, and generate an on-brand concept proof for JD.',
  },
  {
    id: 'championship-pendant',
    name: 'Championship Pendant',
    tagline: 'Pendant companion to the ring',
    engine: 'in-progress',
    priceProduct: 'rings',
    artworkFlow: 'champ-pendant',
    inputs: ['Face & setting', 'Bail & edge', 'Metal', 'Quantity'],
    composes: ['artwork', 'pricing'],
    blurb: 'The pendant companion — priced at the real 93% of ring sell, with its own concept flow.',
  },
  {
    id: 'class-ring',
    name: 'Class Ring',
    tagline: 'Graduation / class ring',
    engine: 'in-progress',
    priceProduct: 'rings',
    artworkFlow: 'sr-grad',
    inputs: ['Size & metal', 'Setting', 'Side shanks', 'Quantity'],
    composes: ['artwork', 'pricing'],
    blurb: 'The scholastic class ring — same real ring engine, its own concept flow.',
  },
  {
    id: 'challenge-coin',
    name: 'Challenge Coin',
    tagline: 'Struck challenge coin',
    engine: 'in-progress',
    priceProduct: 'coins',
    artworkFlow: 'coin',
    inputs: ['Diameter & thickness', 'Metal & plating', 'Quantity (MOQ 100)'],
    composes: ['artwork', 'pricing'],
    blurb: 'Configure diameter, thickness, and metal against the real coin rate table, and generate a coin concept.',
  },
  {
    id: 'replica-ring',
    name: 'Replica Ring',
    tagline: 'Fan / team-store replica',
    engine: 'in-progress',
    priceProduct: 'replicas',
    artworkFlow: 'champ-ring',
    inputs: ['Quantity band (MOQ 50)', 'Packaging'],
    composes: ['pricing', 'artwork'],
    blurb: 'The team-store replica — real quantity-band pricing ($99→$29), with a shared ring concept.',
  },
  {
    id: 'letter-winner-ring',
    name: 'Letter Winner Ring',
    tagline: 'Letterwinner recognition ring',
    engine: 'planned',
    priceProduct: 'letterwinner',
    artworkFlow: 'champ-ring',
    inputs: ['Program tier', 'Size & metal', 'Quantity'],
    composes: ['artwork', 'pricing'],
    blurb: 'The letterwinner program ring — pricing line ports next; the concept flow is live.',
  },
  {
    id: 'lapel-pin',
    name: 'Lapel Pin',
    tagline: 'Die-struck lapel pin',
    engine: 'planned',
    priceProduct: 'pins',
    artworkFlow: null,
    inputs: ['Size & plating', 'Enamel colors', 'Quantity'],
    composes: ['pricing'],
    blurb: 'Die-struck pins with live precious-metal pricing — rate card ports next.',
  },
  {
    id: 'letterman-jacket',
    name: 'Letterman Jacket',
    tagline: 'Body, sleeves, chenille',
    engine: 'planned',
    priceProduct: 'jackets',
    artworkFlow: null,
    inputs: ['Body & sleeve colors', 'Chenille letter', 'Patches', 'Name & year'],
    composes: ['pricing'],
    blurb: 'Build a jacket shell to trim with school colors — pricing line ports next.',
  },
]

export const BUILDER_BY_ID = Object.fromEntries(BUILDERS.map((b) => [b.id, b]))

export const ENGINE_STATUS = {
  connected: { label: 'Live', tone: 'ok' },
  'in-progress': { label: 'Compose live', tone: 'warn' },
  planned: { label: 'Pricing soon', tone: 'muted' },
}

export const COMPOSE_LABEL = {
  artwork: { label: 'Artwork Studio', to: '/artwork-studio' },
  pricing: { label: 'Pricing Engine', to: '/pricing' },
}
